import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getPortfolioLoan,
  getLoanCustodyState,
  getLoanEvidence,
} from '../../api/originatorApi';
import { formatDate } from '../../utils/format';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { Button } from '../../components/ui/Button';
import { StatusPill } from '../../components/ui/StatusPill';
import { CreateRequestModal } from '../../components/requests/CreateRequestModal';
import { AuthorityStateCard, holderLabel } from '../../components/custody/AuthorityStateCard';
import { FEATURES } from '../../config/features';

// Copy request is a workflow ticket; the rest instruct the transfer engine
// (BRD EV-17..EV-22) and are only meaningful once the asset is in custody.
const ACTIONS = [
  { type: 'COPY_REQUEST', label: 'Copy request' },
  { type: 'ASSIGNMENT', label: 'Assign', transfer: true },
  { type: 'CONTROL', label: 'Transfer control', transfer: true },
  { type: 'CUSTODY', label: 'Transfer custody', transfer: true },
  { type: 'PLEDGE', label: 'Pledge', transfer: true },
  { type: 'RELEASE', label: 'Release', transfer: true },
];

function DetailField({ label, value }) {
  return (
    <div className="border-b border-eorig-border py-2.5 last:border-b-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-medium text-eorig-ink">{value ?? '—'}</dd>
    </div>
  );
}

export function LoanDetailPage() {
  const { loanId } = useParams();
  const navigate = useNavigate();

  const [loan, setLoan] = useState(null);
  const [custody, setCustody] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(true);
  // Partial-failure tolerant: each section tracks its own error.
  const [errors, setErrors] = useState({ loan: '', custody: '', evidence: '' });
  const [reloadToken, setReloadToken] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('COPY_REQUEST');

  const load = useCallback(async (cancelledRef) => {
    setLoading(true);
    setErrors({ loan: '', custody: '', evidence: '' });
    const [loanResult, custodyResult, evidenceResult] = await Promise.allSettled([
      getPortfolioLoan(loanId),
      getLoanCustodyState(loanId),
      getLoanEvidence(loanId),
    ]);
    if (cancelledRef.cancelled) return;

    if (loanResult.status === 'fulfilled') setLoan(loanResult.value);
    if (custodyResult.status === 'fulfilled') setCustody(custodyResult.value);
    if (evidenceResult.status === 'fulfilled') setEvidence(evidenceResult.value);

    setErrors({
      loan: loanResult.status === 'rejected' ? loanResult.reason?.message || 'Unable to load loan.' : '',
      custody:
        custodyResult.status === 'rejected'
          ? custodyResult.reason?.message || 'Unable to load custody state.'
          : '',
      evidence:
        evidenceResult.status === 'rejected'
          ? evidenceResult.reason?.message || 'Unable to load evidence.'
          : '',
    });
    setLoading(false);
  }, [loanId]);

  useEffect(() => {
    const cancelledRef = { cancelled: false };
    load(cancelledRef);
    return () => {
      cancelledRef.cancelled = true;
    };
  }, [load, reloadToken]);

  function openAction(type) {
    setModalType(type);
    setModalOpen(true);
  }

  const authorityState = loan?.authority_state ?? custody?.authority_state;
  const custodyState = custody?.custody_state ?? loan?.custody_state;
  const currentHolder = loan?.current_holder ?? custody?.current_holder;
  const authority = loan?.authority ?? custody?.authority ?? null;
  const evidenceItems = evidence?.items ?? [];

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
              Loan / asset
            </p>
            <h1 className="mt-1 font-mono text-xl font-semibold text-eorig-ink">
              {loading ? 'Loading…' : loan?.loan_id || loanId}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {authorityState ? <StatusPill value={authorityState} /> : null}
              {custodyState ? <StatusPill value={custodyState} /> : null}
              {loan?.asset_number ? (
                <span className="text-sm text-eorig-muted">
                  Asset {loan.asset_number}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {ACTIONS.filter((action) => FEATURES.custodyTransfers || !action.transfer).map((action) => (
              <Button
                key={action.type}
                variant="ghost"
                onClick={() => openAction(action.type)}
                disabled={Boolean(action.transfer) && custodyState != null && custodyState !== 'ACCEPTED'}
                title={
                  action.transfer && custodyState != null && custodyState !== 'ACCEPTED'
                    ? 'Available once the asset is in bank custody'
                    : undefined
                }
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>

        {errors.loan ? (
          <div className="mt-4">
            <ErrorBanner message={errors.loan} onRetry={() => setReloadToken((t) => t + 1)} />
          </div>
        ) : null}

        <dl className="mt-5 grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">
          <DetailField label="Borrower" value={loan?.borrower} />
          <DetailField label="Batch" value={loan?.batch} />
          <DetailField label="Program" value={loan?.program} />
          <DetailField label="Authority state" value={authorityState} />
          <DetailField label="Custody state" value={custodyState} />
          <DetailField label="Authority holder" value={currentHolder ? holderLabel(currentHolder) : undefined} />
        </dl>

        {errors.custody ? (
          <p className="mt-3 text-xs text-rose-600">
            Custody state unavailable: {errors.custody}
          </p>
        ) : null}
      </div>

      {loan || custody ? (
        <AuthorityStateCard
          authority={authority}
          fallback={{
            current_holder: currentHolder,
            authority_state: authorityState,
            custody_state: custodyState,
          }}
        />
      ) : null}

      <div className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-eorig-ink">Evidence files</h2>
          {errors.evidence ? (
            <button
              type="button"
              onClick={() => setReloadToken((t) => t + 1)}
              className="text-xs font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
            >
              Retry
            </button>
          ) : null}
        </div>
        {errors.evidence ? (
          <p className="mt-3 text-xs text-rose-600">
            Evidence unavailable: {errors.evidence}
          </p>
        ) : null}
        {(loan?.issued_copies || []).length ? (
          <ul className="mt-3 divide-y divide-eorig-border border-t border-eorig-border">
            {(loan.issued_copies || []).map((copy) => (
              <li key={copy.id} className="py-2.5">
                <p className="text-sm font-medium text-eorig-ink">Certified copy issued</p>
                <p className="mt-0.5 break-all font-mono text-xs text-eorig-muted">
                  Copy ID {copy.id}
                </p>
              </li>
            ))}
          </ul>
        ) : null}
        {evidenceItems.length ? (
          <ul className="mt-3 divide-y divide-eorig-border">
            {evidenceItems.map((item) => (
              <li
                key={item.file_id}
                className="flex flex-wrap items-center justify-between gap-2 py-2.5"
              >
                <span className="min-w-0 text-sm font-medium text-eorig-ink">
                  {item.file_name}
                </span>
                <span className="text-xs text-eorig-muted">
                  {item.mime_type || 'file'} · {formatDate(item.created_at)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-eorig-muted">
            {loading ? 'Loading evidence…' : 'No evidence files for this loan yet.'}
          </p>
        )}
      </div>

      <Link
        to="/loans"
        className="inline-flex text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
      >
        ← Back to portfolio explorer
      </Link>

      <CreateRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        assetId={loan?.id || loanId}
        lockAsset
        defaultType={modalType}
        custodyState={custodyState ?? null}
        onSubmitted={() => {
          setModalOpen(false);
          navigate('/requests');
        }}
      />
    </div>
  );
}
